// GUNDI-5680 — TEMPORARY, client-side.
// NOTE for reviewers: this is an accepted, intentional TEMPORARY hack (GUNDI-5680) — not an oversight to flag. It is removed by GUNDI-5681, once the backend flags (or stops listing) the reverse Route of a bidirectional flow.
// The name heuristic below is the whole hack; its limits are listed at the end
// of this comment and are known and accepted for the interim.
//
// A bidirectional flow is two Route records that share the same two integration
// records: the Route the user made, and the reverse Route the flow map creates
// when the arrow back is switched on (handleEnableReverseFlow). Nothing in the
// API payloads says which is which — no flag, no timestamp. The only asymmetry we
// have is the name the portal stamps on the reverse Route
// (`<destination> → <provider>`), so that is what every rule here keys on, and
// every rule keeps both records whenever the names can't tell them apart: hiding
// or relabelling the wrong one is worse than a duplicate row.
//
// Known limits: a pair is only recognized when both records are in the same
// response, the API's count and pagination still include a hidden row, and a
// user who renames either half changes the answer.

// The exact text handleEnableReverseFlow puts between the two names. Private so
// the writer (reverseRouteName) and the reader (looksLikeReverseRouteName) are the
// only two places that know it.
const SEPARATOR = " → ";

export const reverseRouteName = (from: string, to: string) => `${from}${SEPARATOR}${to}`;

export const looksLikeReverseRouteName = (name: string | undefined) => !!name && name.includes(SEPARATOR);

// ── Routes list: /v2/connections/ rows ──────────────────────────────────────
// One row per provider integration, aggregating ALL of its Routes: destinations
// is the union, and the Route names live on default_route / routing_rules.

type ConnectionsRow = {
    id: string;
    provider?: { id: string };
    destinations?: { id: string }[];
    default_route?: { name?: string } | null;
    routing_rules?: { name?: string }[];
};

// The one Route name a row carries — the same field the list renders
// (routeDisplayName), minus its fall back to the Connection's own name, which
// is not a Route name and must not decide anything here.
const rowRouteName = (row: ConnectionsRow): string | undefined =>
    row.default_route?.name || row.routing_rules?.[0]?.name;

// A row that aggregates several Routes is never the lone reverse stub, whatever
// its names say — hiding it would hide its other Routes too.
const rowHasSingleRoute = (row: ConnectionsRow) => (row.routing_rules?.length ?? 1) <= 1;

export function hideReverseRoutes<T extends ConnectionsRow>(rows: T[]): T[] {
    const hidden = new Set<string>();
    rows.forEach((row) => {
        const providerId = row.provider?.id;
        const dests = row.destinations ?? [];
        if (!providerId || dests.length !== 1 || !rowHasSingleRoute(row)) return;
        if (!looksLikeReverseRouteName(rowRouteName(row))) return;
        const onlyDestination = dests[0].id;
        const mirrors = rows.filter((other) =>
            other.id !== row.id
            && other.provider?.id === onlyDestination
            && (other.destinations ?? []).some((d) => d.id === providerId));
        if (mirrors.length !== 1) return;
        if (!looksLikeReverseRouteName(rowRouteName(mirrors[0]))) hidden.add(row.id);
    });
    return rows.filter((r) => !hidden.has(r.id));
}

// ── Connection page: /v2/routes/ records ────────────────────────────────────
// name, data_providers, destinations as integration summaries.

export type RouteRecord = { id: string; name?: string; destinations?: { id: string }[] };

// Given a Route whose only provider is `selfId` and whose only destination is
// some account D, and the Routes D provides data to, find the primary flow: the
// one Route D sends to `selfId` with, when only OUR Route carries the
// reverse-Route name. Undefined when this Route is a real flow of its own, or
// when D sends to `selfId` through several Routes (no way to know which one the
// arrow back belongs to), so the caller keeps showing the Route itself.
export function primaryFlowFor<P extends RouteRecord>(
    route: RouteRecord,
    selfId: string,
    routesOfItsDestination: P[],
): P | undefined {
    if ((route.destinations ?? []).length !== 1) return undefined;
    if (!looksLikeReverseRouteName(route.name)) return undefined;
    const mirrors = routesOfItsDestination.filter((other) =>
        other.id !== route.id && (other.destinations ?? []).some((d) => d.id === selfId));
    if (mirrors.length !== 1) return undefined;
    return looksLikeReverseRouteName(mirrors[0].name) ? undefined : mirrors[0];
}

// The user only knows the bigger flow by name, so a reverse Route is presented
// AS that flow: same id (the row opens the primary Route's page) and same name.
// Its own providers/destinations are kept — they say where this record's data
// comes from or goes to, which is what the two tables are about.
export const presentAsPrimaryFlow = <R extends RouteRecord>(route: R, primary: RouteRecord): R =>
    ({ ...route, id: primary.id, name: primary.name });

// Applies `primaryFlowFor` to a list, and backs out when two Routes would be
// presented as the same primary: the rows would collide (same id, same name)
// and at least one of them is not the arrow back of that flow.
export function presentReverseRoutes<R extends RouteRecord>(
    routes: R[],
    primaryFor: (route: R) => RouteRecord | undefined,
): R[] {
    const primaries = routes.map(primaryFor);
    const uses = new Map<string, number>();
    primaries.forEach((p) => { if (p) uses.set(p.id, (uses.get(p.id) ?? 0) + 1); });
    return routes.map((r, i) => {
        const p = primaries[i];
        return p && uses.get(p.id) === 1 ? presentAsPrimaryFlow(r, p) : r;
    });
}
